import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Matrix3,
  Matrix4,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  Sphere,
  Vector3,
} from "three";
import type { Material, Mesh, Object3D, Texture } from "three";
import { loadGltf } from "../../city/modelCache.js";
import type { VehicleDims } from "../dims.js";
import { lampGeometry } from "./lamps.js";
import { paintRefOf } from "./paint.js";
import type { PaintRef } from "./paint.js";

/**
 * A vehicle model ready to draw many times: the GLB's meshes merged per material into skinned
 * geometries whose bones are the moving parts (`chassis` -> `body` -> doors and steering wheel;
 * wheels hang off the chassis), so one car costs one draw call per material whatever its part
 * count, and every part still moves. Untextured materials that differ only in colour are merged
 * too (their colour goes into the vertices). A second, much cheaper form for distance: the LOD1
 * model baked into one geometry with the textures sampled into vertex colours (`far`, drawn
 * instanced with the city material).
 */

export const BONE_NAMES = [
  "chassis",
  "body",
  "wheel_fl",
  "wheel_fr",
  "wheel_rl",
  "wheel_rr",
  "wheel_f",
  "wheel_r",
  "door_fl",
  "door_fr",
  "steering_wheel",
] as const;
export type BoneName = (typeof BONE_NAMES)[number];

export interface BoneRest {
  name: BoneName;
  /** Rest position in car space. */
  pivot: Vector3;
  parent: number;
}

export interface Bucket {
  geometry: BufferGeometry;
  material: Material;
  /** Car paint: takes the chosen colour. */
  paint: boolean;
  /** Draws after the opaque parts (glass). */
  transparent: boolean;
  /** Only on the wheels: takes the rim finish. */
  wheel: boolean;
}

export interface CarTemplate {
  stem: string;
  dims: VehicleDims;
  bones: BoneRest[];
  buckets: Bucket[];
  /** How the paint bucket's texture is recoloured (null: untextured paint, set the colour). */
  paintRef: PaintRef | null;
  lamps: BufferGeometry;
  sphere: Sphere;
  triangles: number;
}

/** The parent chain of the moving parts (index into BONE_NAMES order of `bones`). */
const PARENT: Record<BoneName, BoneName | null> = {
  chassis: null,
  body: "chassis",
  wheel_fl: "chassis",
  wheel_fr: "chassis",
  wheel_rl: "chassis",
  wheel_rr: "chassis",
  wheel_f: "chassis",
  wheel_r: "chassis",
  door_fl: "body",
  door_fr: "body",
  steering_wheel: "body",
};

/** The paint's material: "Body", "*_Bodymat", "Paint 1 ..." (not the secondary "Paint 2", not the underbody). */
const isPaintName = (name: string) =>
  /body|paint|bodymat/i.test(name) && !/bottom|paint 2/i.test(name);

type Std = MeshStandardMaterial & {
  transmission?: number;
  clearcoat?: number;
  map: Texture | null;
};

/** Merge key: everything but the base colour (untextured materials then share one draw). */
function keyOf(m: Std): string {
  const tex = [
    m.map,
    m.normalMap,
    m.roughnessMap,
    m.metalnessMap,
    m.emissiveMap,
    m.aoMap,
    m.alphaMap,
  ]
    .map((t) => t?.uuid ?? "-")
    .join(",");
  if (m.map) return `tex:${m.uuid}`;
  return [
    tex,
    m.transparent ? `t${m.opacity.toFixed(2)}` : "o",
    m.alphaTest > 0 ? "a" : "",
    (m.transmission ?? 0) > 0 ? "tr" : "",
    (m.clearcoat ?? 0) > 0 ? "cc" : "",
    m.metalness > 0.5 ? "m" : "d",
    (Math.round(m.roughness * 5) / 5).toFixed(1),
    m.emissive.getHexString(),
    (m.emissiveIntensity ?? 1).toFixed(1),
    m.side,
  ].join("|");
}

/** Builds the template from a loaded scene (pure apart from three: unit tested on a fake scene). */
export function buildTemplate(
  scene: Object3D,
  stem: string,
  dims: VehicleDims,
  paintDefault: string,
): CarTemplate {
  scene.updateMatrixWorld(true);
  // Which bones the model has: named nodes of BONE_NAMES; always chassis and body.
  const present = new Set<BoneName>(["chassis", "body"]);
  const pivots = new Map<BoneName, Vector3>();
  scene.traverse((o) => {
    const n = o.name as BoneName;
    if ((BONE_NAMES as readonly string[]).includes(n) && n !== "chassis" && n !== "body") {
      present.add(n);
      pivots.set(n, new Vector3().setFromMatrixPosition(o.matrixWorld));
    }
  });
  pivots.set("chassis", new Vector3(0, 0, 0));
  pivots.set("body", new Vector3(0, dims.height * 0.42, 0));
  const names = BONE_NAMES.filter((n) => present.has(n));
  const bones: BoneRest[] = names.map((name) => ({
    name,
    pivot: pivots.get(name)!.clone(),
    parent: PARENT[name] ? names.indexOf(PARENT[name]) : -1,
  }));
  const boneOf = (o: Object3D): number => {
    for (let a: Object3D | null = o; a; a = a.parent) {
      const i = names.indexOf(a.name as BoneName);
      if (i >= 2) return i;
    }
    return 1; // body
  };

  interface Acc {
    material: Std;
    pos: number[];
    nor: number[];
    uv: number[];
    col: number[];
    skin: number[];
    index: number[];
    vertexColors: boolean;
    paint: boolean;
    bones: Set<number>;
  }
  const accs = new Map<string, Acc>();
  const v = new Vector3();
  const n3 = new Matrix3();
  let triangles = 0;
  const meshes: Mesh[] = [];
  scene.traverse((o) => {
    if ((o as Mesh).isMesh) meshes.push(o as Mesh);
  });
  const paintMeshes = meshes.filter((m) => isPaintName(materialOf(m).name));
  const single = new Set(meshes.map((m) => materialOf(m).uuid)).size === 1;
  for (const mesh of meshes) {
    const mat = materialOf(mesh) as Std;
    const g = mesh.geometry;
    const p = g.getAttribute("position");
    if (!p) continue;
    const nm = g.getAttribute("normal");
    const uvA = g.getAttribute("uv");
    const key = (isPaintName(mat.name) ? "P|" : "") + keyOf(mat);
    let acc = accs.get(key);
    if (!acc) {
      acc = {
        material: mat,
        pos: [],
        nor: [],
        uv: [],
        col: [],
        skin: [],
        index: [],
        vertexColors: false,
        paint: single || (paintMeshes.length > 0 && isPaintName(mat.name)),
        bones: new Set(),
      };
      // Untextured non-paint materials carry their colour in the vertices (so they merge).
      acc.vertexColors = !mat.map && !acc.paint;
      accs.set(key, acc);
    } else if (acc.material !== mat) {
      // Merged with another colour: the bucket's paint flag only if both are paint.
      acc.paint = acc.paint && isPaintName(mat.name);
    }
    const base = acc.pos.length / 3;
    const bone = boneOf(mesh);
    acc.bones.add(bone);
    n3.getNormalMatrix(mesh.matrixWorld);
    const c = new Color().copy(mat.color);
    for (let i = 0; i < p.count; i++) {
      v.fromBufferAttribute(p, i).applyMatrix4(mesh.matrixWorld);
      acc.pos.push(v.x, v.y, v.z);
      if (nm) v.fromBufferAttribute(nm, i).applyMatrix3(n3).normalize();
      else v.set(0, 1, 0);
      acc.nor.push(v.x, v.y, v.z);
      acc.uv.push(uvA ? uvA.getX(i) : 0, uvA ? uvA.getY(i) : 0);
      acc.col.push(c.r, c.g, c.b);
      acc.skin.push(bone);
    }
    const idx = g.index;
    if (idx) for (let i = 0; i < idx.count; i++) acc.index.push(base + idx.getX(i));
    else for (let i = 0; i < p.count; i++) acc.index.push(base + i);
    triangles += (idx ? idx.count : p.count) / 3;
  }

  const buckets: Bucket[] = [];
  let paintRef: PaintRef | null = null;
  for (const acc of accs.values()) {
    const geometry = new BufferGeometry();
    const count = acc.pos.length / 3;
    geometry.setAttribute("position", new BufferAttribute(new Float32Array(acc.pos), 3));
    geometry.setAttribute("normal", new BufferAttribute(new Float32Array(acc.nor), 3));
    geometry.setAttribute("uv", new BufferAttribute(new Float32Array(acc.uv), 2));
    if (acc.vertexColors)
      geometry.setAttribute("color", new BufferAttribute(new Float32Array(acc.col), 3));
    const si = new Uint16Array(count * 4);
    const sw = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      si[i * 4] = acc.skin[i]!;
      sw[i * 4] = 1;
    }
    geometry.setAttribute("skinIndex", new BufferAttribute(si, 4));
    geometry.setAttribute("skinWeight", new BufferAttribute(sw, 4));
    geometry.setIndex(
      new BufferAttribute(
        count > 65535 ? new Uint32Array(acc.index) : new Uint16Array(acc.index),
        1,
      ),
    );
    geometry.computeBoundingSphere();
    let material: Std = acc.material.clone();
    if (acc.vertexColors) {
      material.vertexColors = true;
      material.color.set(1, 1, 1);
    }
    if (acc.paint && !(material as Partial<MeshPhysicalMaterial>).isMeshPhysicalMaterial) {
      // Car paint gets a clear coat (the source did not have one).
      const phys = new MeshPhysicalMaterial();
      MeshStandardMaterial.prototype.copy.call(phys, material);
      phys.clearcoat = 1;
      phys.clearcoatRoughness = 0.08;
      material.dispose();
      material = phys;
    }
    if (!material.transparent) material.side = DoubleSide;
    if (acc.paint && material.map) paintRef = paintRefOf(material.map, paintDefault);
    buckets.push({
      geometry,
      material,
      paint: acc.paint,
      transparent: material.transparent || (material.transmission ?? 0) > 0,
      wheel:
        !single &&
        acc.bones.size > 0 &&
        [...acc.bones].every((b) => names[b]?.startsWith("wheel_") ?? false),
    });
  }
  // Opaque first, glass after (the renderer sorts too; this keeps the order stable).
  buckets.sort((a, b) => Number(a.transparent) - Number(b.transparent));
  const d = dims;
  const sphere = new Sphere(
    new Vector3(0, d.height / 2, (d.bounds.max[2]! + d.bounds.min[2]!) / 2),
    Math.hypot(d.length / 2 + 1.2, d.height / 2, d.width / 2 + 1.2),
  );
  return {
    stem,
    dims,
    bones,
    buckets,
    paintRef,
    lamps: lampGeometry(dims),
    sphere,
    triangles: Math.round(triangles),
  };
}

const materialOf = (m: Mesh): Material =>
  Array.isArray(m.material) ? (m.material[0] as Material) : m.material;

const templates = new Map<string, Promise<CarTemplate>>();

/** One load and build per model file, shared by every car of that model. */
export function loadTemplate(
  file: string,
  dims: VehicleDims,
  paintDefault: string,
): Promise<CarTemplate> {
  let p = templates.get(file);
  if (!p) {
    const stem = file.replace(/^.*\//, "").replace(/\.glb$/i, "");
    p = loadGltf(file).then((g) => buildTemplate(g.scene, stem, dims, paintDefault));
    p.catch(() => templates.delete(file));
    templates.set(file, p);
  }
  return p;
}

/** Rest transforms of the bones relative to their parents, and the inverse binds. */
export function restOf(t: CarTemplate): { local: Vector3[]; inverses: Matrix4[] } {
  const local = t.bones.map((b) =>
    b.parent >= 0 ? b.pivot.clone().sub(t.bones[b.parent]!.pivot) : b.pivot.clone(),
  );
  const inverses = t.bones.map((b) =>
    new Matrix4().makeTranslation(-b.pivot.x, -b.pivot.y, -b.pivot.z),
  );
  return { local, inverses };
}
